/**
 * Social Media Post Sorting Utilities
 *
 * Provides flexible sorting for posts based on different date fields
 * and user preferences.
 *
 * Sorting Behaviors:
 * - Smart Sort (relevant_date): Uses the most contextually relevant date for each post
 *   • Published posts → sorted by publish date
 *   • Scheduled posts → sorted by schedule date
 *   • Draft posts → sorted by creation date
 *
 * - Date Created: Sorts all posts by creation timestamp regardless of status
 *
 * - Schedule Date: Sorts by scheduled publish time
 *   • Posts without schedule dates (drafts, already published) appear at the bottom
 *
 * - Publish Date: Sorts by actual publish timestamp
 *   • Unpublished posts (drafts, scheduled) appear at the bottom
 *
 * - Status: Groups posts by status priority for workflow management
 */
export type SortField =
  | 'relevant_date'
  | 'created_at'
  | 'scheduled_time'
  | 'published_at'
  | 'status';
export type SortOrder = 'asc' | 'desc';

export interface SortOptions {
  field: SortField;
  order: SortOrder;
}

export interface SortablePost {
  created_at: string
  scheduled_time: string | null
  published_at: string | null
  status: string
}

/**
 * Get the most relevant date for a post based on its status
 * - Published posts: use published_at
 * - Scheduled posts: use scheduled_time
 * - All others: use created_at
 */
export function getRelevantDate<T extends SortablePost>(post: T): Date {
  if (post.published_at) {
    return new Date(post.published_at);
  }
  if (
    post.scheduled_time &&
    (post.status === 'SCHEDULED' || post.status === 'PROCESSING')
  ) {
    return new Date(post.scheduled_time);
  }
  return new Date(post.created_at);
}

/**
 * Sort posts by the most relevant date field
 */
export function sortByRelevantDate(
  posts: SortablePost[],
  order: SortOrder = 'desc'
): SortablePost[] {
  return [...posts].sort((a, b) => {
    const dateA = getRelevantDate(a).getTime();
    const dateB = getRelevantDate(b).getTime();
    return order === 'desc' ? dateB - dateA : dateA - dateB;
  });
}

/**
 * Sort posts by a specific field
 */
export function sortPosts<T extends SortablePost>(
  posts: T[],
  options: SortOptions
): T[] {
  const { field, order } = options;

  return [...posts].sort((a, b) => {
    let compareValue = 0;

    switch (field) {
      case 'relevant_date':
        {
          const dateA = getRelevantDate(a).getTime();
          const dateB = getRelevantDate(b).getTime();
          compareValue = dateB - dateA;
        }
        break;

      case 'created_at':
        compareValue =
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        break;

      case 'scheduled_time':
        // Handle nulls - posts without scheduled_time (drafts, published posts) go to the end
        // This sort is primarily for viewing scheduled posts in order
        if (!a.scheduled_time && !b.scheduled_time) {
          // If both have no schedule, maintain original order
          return 0;
        }
        if (!a.scheduled_time) return 1; // a goes after b
        if (!b.scheduled_time) return -1; // b goes after a
        compareValue =
          new Date(b.scheduled_time).getTime() -
          new Date(a.scheduled_time).getTime();
        break;

      case 'published_at':
        // Handle nulls - unpublished posts (drafts, scheduled) go to the end
        // This sort is primarily for viewing published posts history
        if (!a.published_at && !b.published_at) {
          // If both unpublished, maintain original order
          return 0;
        }
        if (!a.published_at) return 1; // a goes after b
        if (!b.published_at) return -1; // b goes after a
        compareValue =
          new Date(b.published_at).getTime() -
          new Date(a.published_at).getTime();
        break;

      case 'status':
        // Custom status order: ERROR > PROCESSING > SCHEDULED > DRAFT > PUBLISHED > CANCELLED
        {
          const statusOrder: Record<string, number> = {
            ERROR: 0,
            FAILED: 0,
            PROCESSING: 1,
            SCHEDULED: 2,
            DRAFT: 3,
            PUBLISHED: 4,
            POSTED: 4,
            CANCELLED: 5,
          };
          const orderA = statusOrder[a.status] ?? 99;
          const orderB = statusOrder[b.status] ?? 99;
          compareValue = orderA - orderB;
        }
        break;

      default:
        return 0;
    }

    return order === 'desc' ? compareValue : -compareValue;
  });
}

/**
 * Group posts by date for calendar/timeline views
 */
export function groupPostsByDate(
  posts: SortablePost[]
): Map<string, SortablePost[]> {
  const grouped = new Map<string, SortablePost[]>();

  posts.forEach((post) => {
    const date = getRelevantDate(post);
    const dateKey = date.toISOString().split('T')[0]; // YYYY-MM-DD

    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, []);
    }
    grouped.get(dateKey)!.push(post);
  });

  // Sort posts within each date group by time
  grouped.forEach((postsInDay) => {
    postsInDay.sort((a, b) => {
      const timeA = getRelevantDate(a).getTime();
      const timeB = getRelevantDate(b).getTime();
      return timeB - timeA;
    });
  });

  return grouped;
}

/**
 * Get upcoming scheduled posts
 */
export function getUpcomingPosts(
  posts: SortablePost[],
  limitDays: number = 7
): SortablePost[] {
  const now = new Date();
  const futureLimit = new Date();
  futureLimit.setDate(futureLimit.getDate() + limitDays);

  return posts
    .filter((post) => {
      if (post.status !== 'SCHEDULED' || !post.scheduled_time) return false;
      const scheduledDate = new Date(post.scheduled_time);
      return scheduledDate >= now && scheduledDate <= futureLimit;
    })
    .sort((a, b) => {
      const dateA = new Date(a.scheduled_time!).getTime();
      const dateB = new Date(b.scheduled_time!).getTime();
      return dateA - dateB; // Ascending order for upcoming posts
    });
}

/**
 * Get overdue scheduled posts
 */
export function getOverduePosts<T extends SortablePost>(posts: T[]): T[] {
  const now = new Date();

  return posts
    .filter((post) => {
      if (post.status !== 'SCHEDULED' || !post.scheduled_time) return false;
      const scheduledDate = new Date(post.scheduled_time);
      return scheduledDate < now;
    })
    .sort((a, b) => {
      const dateA = new Date(a.scheduled_time!).getTime();
      const dateB = new Date(b.scheduled_time!).getTime();
      return dateB - dateA; // Most recent overdue first
    });
}
